import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown, LineChart as LineIcon, Maximize2, Minimize2, ExternalLink } from "lucide-react";
import { GameLogChart } from "./GameLogChart";
import { TeamLogo, GameLabel } from "./TeamLogo";
import { AITrendPanel } from "./AITrendPanel";
import { ChartControls } from "./ChartControls";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";

const TIMEFRAMES = [
  { key: "L5", label: "L5", season: "combined", n: 5 },
  { key: "L10", label: "L10", season: "combined", n: 10 },
  { key: "L15", label: "L15", season: "combined", n: 15 },
  { key: "ThisSeason", label: "This Season", season: "current", n: null },
  { key: "LastSeason", label: "Last Season", season: "last", n: null },
];

// least-squares linear regression -> trend value per index
const regression = (ys) => {
  const n = ys.length;
  if (n < 2) return ys.map((y) => y);
  const xs = ys.map((_, i) => i);
  const sx = xs.reduce((a, b) => a + b, 0);
  const sy = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((a, x, i) => a + x * ys[i], 0);
  const sxx = xs.reduce((a, x) => a + x * x, 0);
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
  const intercept = (sy - slope * sx) / n;
  return xs.map((x) => Math.max(0, Math.round((intercept + slope * x) * 10) / 10));
};

// Simple moving average; null until `n` real games exist.
const movingAvg = (ys, n) =>
  ys.map((_, i) => (i + 1 < n ? null : Math.round((ys.slice(i + 1 - n, i + 1).reduce((a, b) => a + b, 0) / n) * 10) / 10));

const VIEW_KEY = "pm_chart_view";
const loadView = () => {
  try {
    return { type: "bar", ma: null, fib: false, ...JSON.parse(localStorage.getItem(VIEW_KEY) || "{}") };
  } catch (_) {
    return { type: "bar", ma: null, fib: false };
  }
};

const MetricChart = ({ prop, frame, nextGame, view, toolbar, onExpand, startExpanded = false }) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const cur = prop.game_log_current || [];
  const last = prop.game_log_last || [];
  // L5/L10/L15 roll across the season boundary: last season (older) then this season (newer).
  const tag = (rows, season) => rows.map((r) => ({ ...r, season }));
  const source =
    frame.season === "last" ? tag(last, "last") : frame.season === "current" ? tag(cur, "cur")
      : [...tag(last, "last"), ...tag(cur, "cur")];
  const full = source && source.length ? source : [];
  const window = frame.n ? full.slice(-frame.n) : full;
  const values = window.map((d) => d.v);
  const trend = regression(values);
  // Moving average over the full real history so early bars in the window still get a value.
  const ma = view.ma ? movingAvg(full.map((d) => d.v), view.ma) : [];
  const offset = full.length - window.length;
  const data = window.map((d, i) => ({ ...d, t: trend[i], ma: view.ma ? ma[offset + i] : undefined }));
  const maNext = view.ma && full.length >= view.ma ? ma[ma.length - 1] : null;
  const hits = values.filter((v) => v > prop.line).length;
  const hitRate = values.length ? Math.round((hits / values.length) * 100) : 0;
  const hitColor = hitRate >= 60 ? "#10B981" : hitRate >= 45 ? "#F59E0B" : "#EF4444";
  const over = prop.lean === "OVER";
  const rising = trend.length > 1 && trend[trend.length - 1] >= trend[0];
  const avg = values.length ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10 : "—";

  const slug = prop.metric.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const toggleExpand = () => (onExpand ? onExpand(prop.id) : setExpanded((e) => !e));

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setExpanded(false);
    globalThis.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      globalThis.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  const card = (
    <div
      data-testid={expanded ? `metric-chart-fullscreen-${slug}` : `metric-chart-${slug}`}
      className={
        expanded
          ? "fixed inset-0 z-[70] overflow-y-auto bg-[#0B0F17] p-4 sm:p-8 space-y-4"
          : "rounded-xl border border-[#233248] bg-[#0B0F17]/50 p-3"
      }
    >
      {expanded && toolbar && <div className="space-y-3 rounded-xl border border-[#233248] bg-[#141C2B] p-3">{toolbar}</div>}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-white text-sm">{prop.metric}</span>
          <span className="font-data text-[11px] text-slate-400">line {prop.line}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {data.length > 0 && (
            <span
              className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold font-data ${
                rising ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"
              }`}
              data-testid={`trend-badge-${prop.id}`}
            >
              <LineIcon className="h-3 w-3" /> {rising ? "Trending up" : "Trending down"}
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold font-data ${
              over ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"
            }`}
          >
            {over ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {prop.lean}
          </span>
          <button
            data-testid={expanded ? `close-fullscreen-${prop.id}` : `expand-chart-${prop.id}`}
            onClick={expanded ? () => setExpanded(false) : toggleExpand}
            aria-label={expanded ? "Exit full screen" : "Expand chart"}
            className={expanded
              ? "inline-flex items-center gap-1.5 rounded-lg border border-[#233248] bg-[#141C2B] px-3 py-1.5 text-xs font-semibold text-white hover:border-[#00E5FF]/50 transition-colors"
              : "grid place-items-center h-6 w-6 rounded-md text-slate-400 hover:text-white hover:bg-[#1B273E] transition-colors"}
          >
            {expanded ? <><Minimize2 className="h-3.5 w-3.5" /> Exit full screen</> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {data.length === 0 ? (
        <div
          data-testid={`metric-chart-nodata-${prop.id}`}
          className="flex flex-col items-center justify-center gap-1 py-8 text-center"
        >
          <LineIcon className="h-5 w-5 text-slate-600" />
          <p className="text-xs text-slate-500">Not enough game data yet for {frame.label}.</p>
          <p className="text-[10px] text-slate-600">Only real game logs are shown here.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-2">
            {[
              [`${frame.label} Avg`, avg],
              ["Proj", prop.projection],
              ["Line", prop.line],
              [`${frame.label} Hit`, `${hitRate}%`],
            ].map(([l, v], i) => (
              <div key={l} className="text-center">
                <div className="font-data font-bold text-sm" style={i === 3 ? { color: hitColor } : { color: "#fff" }}>
                  {v}
                </div>
                <div className="text-[10px] text-slate-500 truncate">{l}</div>
              </div>
            ))}
          </div>

          {maNext != null && (
            <div data-testid={`ma-projection-${prop.id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-[#F43F5E]/30 bg-[#F43F5E]/10 px-2.5 py-1.5 mb-2">
              <span className="text-[11px] text-slate-300">
                <span className="font-data font-bold text-[#F43F5E]">MA{view.ma}</span> projects next game
              </span>
              <span className="font-data text-sm font-bold text-white">
                {maNext}{" "}
                <span className={maNext > prop.line ? "text-[#10B981]" : "text-[#EF4444]"}>
                  {maNext > prop.line ? "OVER" : "UNDER"} {prop.line}
                </span>
              </span>
            </div>
          )}

          <GameLogChart prop={prop} rows={data} metricLabel={prop.metric} view={view} maNext={maNext} fullscreen={expanded}
            nextGame={frame.season !== "last" ? nextGame : null} />
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 flex-wrap">
            {view.type === "bar" ? (
              <>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#10B981]" /> over line</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#EF4444]" /> under line</span>
              </>
            ) : (
              <span className="flex items-center gap-1"><span className="h-2 w-3 border-t-2 border-[#3B82F6]" /> game value</span>
            )}
            <span className="flex items-center gap-1"><span className="h-2 w-3 border-t-2 border-dashed border-[#FACC15]" /> prop line</span>
            {view.trend !== false && (
              <span className="flex items-center gap-1"><span className="h-2 w-3 border-t-2 border-[#FB923C]" /> trend</span>
            )}
            {view.ma && <span className="flex items-center gap-1"><span className="h-2 w-3 border-t-2 border-[#F43F5E]" /> MA{view.ma}</span>}
            {view.fib && <span className="flex items-center gap-1"><span className="h-2 w-3 border-t-2 border-[#22D3EE]" /> Fibonacci (window high → low)</span>}
          </div>
        </>
      )}
    </div>
  );
  return expanded ? createPortal(card, document.body) : card;
};

export const usePlayerAnalysisState = () => {
  const [frame, setFrame] = useState(TIMEFRAMES[1]); // default L10
  const [view, setViewState] = useState(loadView);
  const setView = (v) => {
    localStorage.setItem(VIEW_KEY, JSON.stringify(v));
    setViewState(v);
  };
  return { frame, setFrame, view, setView };
};

export const AnalysisToolbar = ({ frame, setFrame, view, setView }) => (
  <>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <p className="text-xs text-slate-400 max-w-2xl">
        Real game-by-game logs — switch Bars / Line, add a Moving Average to project the next game, or Fibonacci levels from the window's high and low. Green = cleared the line.
      </p>
      <div data-testid="timeframe-selector" className="inline-flex rounded-lg border border-[#233248] bg-[#0B0F17] p-0.5 flex-wrap">
        {TIMEFRAMES.map((t) => (
          <button
            key={t.key}
            data-testid={`timeframe-${t.key.toLowerCase()}`}
            onClick={() => setFrame(t)}
            className={`rounded-md px-2.5 py-1 text-[11px] font-data font-semibold transition-colors whitespace-nowrap ${
              frame.key === t.key ? "bg-[#00E5FF] text-[#0B0F17]" : "text-slate-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
    <ChartControls view={view} onChange={setView} />
  </>
);

export const MetricCharts = ({ player, frame, view, columns = 1, toolbar, onExpand, expandId }) => (
  <div className={columns === 2 ? "grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" : "space-y-3"}>
    {player.props.map((p) => (
      <MetricChart key={p.id} prop={p} frame={frame} nextGame={player.next_game} view={view}
        toolbar={toolbar} onExpand={onExpand} startExpanded={p.id === expandId} />
    ))}
    {player.props.length === 0 && (
      <p className="text-sm text-slate-500 text-center py-6">No stat lines posted for this player yet.</p>
    )}
  </div>
);

export const PlayerProfileModal = ({ playerId, open, onClose }) => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const st = usePlayerAnalysisState();
  useEffect(() => {
    if (open && playerId) {
      setLoading(true);
      setPlayer(null);
      api.get(`/players/${playerId}`).then((r) => setPlayer(r.data)).finally(() => setLoading(false));
    }
  }, [open, playerId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="player-profile-modal"
        className="bg-[#141C2B] border-[#233248] text-white max-w-2xl max-h-[88vh] overflow-y-auto"
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
          </div>
        )}
        {player && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-3 pr-8">
                <TeamLogo sport={player.sport} abbr={player.team_abbr} color={player.color} size="xl" />
                <div className="flex-1 min-w-0">
                  <div>{player.name}</div>
                  <div className="font-data text-xs text-slate-400 font-normal flex items-center gap-1">
                    {player.position} · {player.game_label ? <GameLabel sport={player.sport} label={player.game_label} size="sm" /> : null}
                  </div>
                </div>
                <button
                  data-testid="open-player-full-page"
                  onClick={() => { onClose(); nav(`/player/${player.id}`); }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-2.5 py-1.5 text-xs font-semibold text-[#00E5FF] hover:bg-[#00E5FF]/20 transition-colors shrink-0"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Full page
                </button>
              </DialogTitle>
            </DialogHeader>

            <AnalysisToolbar {...st} />
            <AITrendPanel player={player} frame={st.frame} />
            <MetricCharts player={player} frame={st.frame} view={st.view}
              onExpand={(pid) => { onClose(); nav(`/player/${player.id}?expand=${encodeURIComponent(pid)}`); }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
